



















































import { computed, defineComponent, onMounted, ref, watch } from '@vue/composition-api';
import ObjectId from 'bson-objectid';
import DepthsForm from '@/components/forms/DepthsForm/DepthsForm.vue';
import QRScanner from '@/components/qr-scanner/QRScanner.vue';
import { useEditSample } from '@/store/modules/samples';
import { Location, Sample, Sampling, SamplingCollection, SoDepth } from '@/store/types';

function createSoDepth() {
	return {
		minValue: {
			value: 0,
			unit: 'unit:CM',
		},
		maxValue: {
			value: 0,
			unit: 'unit:CM',
		},
	};
}

export default defineComponent({
	components: {
		DepthsForm,
		'qr-scanner': QRScanner,
	},
	props: {
		sample: {
			type: Object as () => Sample,
		},
		sampling: {
			type: Object as () => Sampling,
		},
		samplingCollection: {
			type: Object as () => SamplingCollection,
		},
		location: {
			type: Object as () => Location,
		},
		submitted: {
			type: Boolean,
		},
	},
	setup(props, { root, emit }) {
		const label = ref('');
		const depth = ref();
		const depthSelectValue = ref();
		const sampleForm = ref();
		const isValidForm = ref();
		const { editSample, clearError, isLoading, hasError } = useEditSample(root.$store);

		const isSubmitted = computed<boolean>(
			() =>
				root.$store.getters.getSamplingCollectionStatusBySamplingCollectionId(props.samplingCollection?.id) ===
				'SUBMITTED'
		);

		function onDepthSelect(val: string | SoDepth) {
			if (val === 'custom') {
				depth.value = createSoDepth();
			} else if (typeof val !== 'string') {
				depth.value = val;
			}
		}

		const depths = computed(() => {
			const items =
				props.samplingCollection?.sampleDepths.map((d: SoDepth) => ({
					text: `${d.minValue.value} - ${d.maxValue.value} cm`,
					value: d,
				})) || [];
			return [
				...items,
				{
					text: 'Custom',
					value: 'custom',
				},
			];
		});

		function initialize(sample?: Sample) {
			label.value = sample?.name || '';
			depth.value = sample?.soDepth || undefined;

			const depthIsStandard = props.samplingCollection?.sampleDepths.some(
				(d: SoDepth) =>
					d.maxValue.value === sample?.soDepth.maxValue.value && d.minValue.value === sample?.soDepth.minValue.value
			);

			if (sample && depthIsStandard) {
				depthSelectValue.value = sample.soDepth;
			} else if (sample && !depthIsStandard) {
				depthSelectValue.value = 'custom';
				depth.value = sample.soDepth;
			} else {
				depthSelectValue.value = undefined;
			}
			sampleForm.value.resetValidation();
		}

		onMounted(() => {
			initialize(props.sample);
		});

		watch(
			() => props.sample,
			(sample) => initialize(sample)
		);

		async function onSubmit() {
			if (!sampleForm.value.validate()) {
				return;
			}

			const newSample = {
				...props.sample,
				id: props.sample?.id || new ObjectId().toHexString(),
				name: label.value,
				resultOf: props.sampling?.id,
				soDepth: { ...depth.value },
			} as Sample;

			let success = false;
			if (props.submitted) {
				clearError();

				success = await editSample({
					id: newSample.id,
					name: newSample.name,
					soDepth: newSample.soDepth,
				});

				if (success) {
					root.$store.dispatch('resources/setSample', newSample);
					clearError();
				}
			} else {
				success = true;
			}

			if (success) {
				emit('setSample', newSample);
				sampleForm.value.reset();
			}
		}

		function onSetDepths(depths: SoDepth[]) {
			depth.value = depths[0];
			sampleForm.value.validate();
		}

		function onCancel() {
			emit('cancelSample');
			initialize(props.sample);
		}

		function onDelete() {
			emit('deleteSample', props.sample?.id);
			sampleForm.value.reset();
		}

		function onCodeDetected(val: string) {
			label.value = val;
		}

		return {
			depths,
			onSubmit,
			onDepthSelect,
			depth,
			depthSelectValue,
			label,
			createSoDepth,
			onSetDepths,
			sampleForm,
			onCancel,
			onDelete,
			onCodeDetected,
			isValidForm,
			isSubmitted,
			isLoading,
			hasError,
		};
	},
});
